<template>
    <div class="chart">
        <v-chart
            ref="chart"
            :option="options"
            autoresize
            @click="onChartClick"
        />
    </div>
</template>
<script>
import { date } from 'quasar';
import { convertDurationToDHMS } from '@utils/time.js';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import { colors } from 'quasar';
const { getPaletteColor } = colors;
import { sanitizeString } from '@utils/sanitize.js';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
    TooltipComponent,
    GridComponent,
    TitleComponent,
    DataZoomComponent
} from 'echarts/components';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

use([
    CanvasRenderer,
    BarChart,
    TooltipComponent,
    GridComponent,
    TitleComponent,
    DataZoomComponent
]);

export default {
    name: 'PhasesGraph',
    components: { VChart },
    emits: ['phaseClick'],
    props: {
        /**
         * Whether the graph is loading.
         */
        isLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * The data to display in the graph.
         */
        phases: {
            type: Array,
            required: true
        },
        /**
         * The start date of the graph.
         */
        startDate: {
            type: String,
            required: true
        },
        /**
         * The end date of the graph.
         */
        endDate: {
            type: String,
            required: true
        },
        /**
         * The period of the graph.
         */
        graphPeriodInHours: {
            type: Number,
            required: true
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * The start date of the graph.
         * @returns {Date} The start date of the graph.
         */
        getStartDate() {
            return new Date(this.startDate);
        },
        /**
         * The end date of the graph.
         * @returns {Date} The end date of the graph.
         */
        getEndDate() {
            return new Date(this.endDate);
        },
        /**
         * The options for the graph.
         * @returns {object} The options for the graph.
         */
        options() {
            return {
                grid: {
                    top: 0,
                    left: 5,
                    right: 5,
                    bottom: 10,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                tooltip: {
                    trigger: 'item'
                },
                animation: false,
                xAxis: {
                    type: 'value',
                    max: this.chartTotalDuration,
                    interval: {
                        show: false // don't show vertical grid lines
                    },
                    axisLabel: {
                        alignMinLabel: 'left',
                        alignMaxLabel: 'right',
                        formatter: value => this.formatXAxisLabel(value)
                    },
                    splitLine: {
                        show: true
                    }
                },
                yAxis: {
                    type: 'category',
                    show: false
                },
                series: this.seriesData
            };
        },
        /**
         * Format the phases array to display it in the graph.
         * Reverse the array to display the phases in the right order.
         * @returns {array} The phases in reverse order.
         */
        formattedPhases() {
            // if there is only one phase return it
            if (this.phases.length === 1) {
                const phase = this.phases[0];
                const totalGraphDuration = this.graphPeriodInHours * 60 * 60;
                return [
                    {
                        start: phase.start,
                        // in case of an ongoing phase set end date to null
                        end: phase.end || null,
                        status: phase.phase,
                        duration: totalGraphDuration,
                        exceededDuration:
                            Math.abs(phase.duration_s) - totalGraphDuration,
                        product: phase.product_name || null,
                        cause: phase.cause_name || null
                    }
                ];
            }

            const phases = this.phases.map(phase => {
                const phaseObj = {
                    start: phase.start,
                    // in case of an ongoing phase set end date to null
                    end: phase.end || null,
                    status: phase.phase,
                    // duration must be positive
                    duration: Math.abs(phase.duration_s),
                    exceededDuration: 0,
                    product: phase.product_name || null,
                    cause: phase.cause_name || null
                };

                // trim the duration of the phase if it exceeds the start date
                if (new Date(phaseObj.start) < this.getStartDate) {
                    // get total of seconds between start date and phase start date
                    const seconds = date.getDateDiff(
                        this.getStartDate,
                        phaseObj.start,
                        'seconds'
                    );
                    // remove extra seconds from the duration and return a positive number
                    phaseObj.duration = Math.abs(phase.duration_s - seconds);
                    // store the exceeded duration to display the total duration in the tooltip
                    phaseObj.exceededDuration = seconds;
                }

                // trim the duration of the phase if it exceeds the end date
                if (new Date(phaseObj.end) > this.getEndDate) {
                    // get total of seconds between end date and phase end date
                    const seconds = date.getDateDiff(
                        phaseObj.end,
                        this.getEndDate,
                        'seconds'
                    );
                    // remove extra seconds from the duration and return a positive number
                    phaseObj.duration = Math.abs(phase.duration_s - seconds);
                    // store the exceeded duration to display the total duration in the tooltip
                    phaseObj.exceededDuration = seconds;
                }

                // trim the duration of the phase if it is ongoing
                if (phaseObj.end === null) {
                    const seconds = date.getDateDiff(
                        phaseObj.start,
                        this.getStartDate,
                        'seconds'
                    );
                    // get the new duration by removing the extra seconds from the total duration of the graph
                    const newDuration = Math.abs(
                        this.graphPeriodInHours * 60 * 60 - seconds
                    );
                    phaseObj.duration = newDuration;

                    // store the exceeded duration to display the total duration in the tooltip
                    phaseObj.exceededDuration = Math.abs(
                        phase.duration_s - newDuration
                    );
                }

                return phaseObj;
            });

            return phases.reverse();
        },
        /**
         * Get the total duration of the phases in minutes.
         * To display the dates on the x axis labels.
         * @returns {number} The total duration of the phases in minutes.
         */
        chartTotalDuration() {
            if (this.formattedPhases.length === 0) {
                return 0;
            }

            // loop thought the phases adding the duration of each phase
            let chartDuration = 0;
            this.formattedPhases.forEach(phase => {
                chartDuration += this.secondsToMinutes(phase.duration);
            });

            // if chart duration is greater than the graphPeriodInHours set it to the graphPeriodInHours
            const graphPeriodInMinutes = this.graphPeriodInHours * 60;
            if (chartDuration > graphPeriodInMinutes) {
                chartDuration = graphPeriodInMinutes;
            }

            return chartDuration;
        },
        /**
         * Get series list for the graph.
         * @returns {array} The series list for the graph.
         */
        seriesData() {
            const data = [];

            if (this.formattedPhases.length === 0) {
                return [];
            }

            this.formattedPhases.forEach((phase, index) => {
                const duration = this.secondsToMinutes(phase.duration);
                data.push({
                    name: this.$t(ASSET_STATUS_TO_STYLE[phase.status]['label']),
                    type: 'bar',
                    stack: 'total',
                    label: {
                        show: true,
                        formatter: _ => {
                            // don't display label on mobile
                            if (this.$q.screen.lt.sm) {
                                return '';
                            }
                            // don't display label if duration is less than 1 hour
                            if (duration < 60) {
                                return '';
                            }
                            // display an interrogation mark if phase is in downtime and cause is not set
                            if (
                                phase.status === 'in_downtime' &&
                                phase.cause === null
                            ) {
                                return '!';
                            }
                            return this.$t(
                                ASSET_STATUS_TO_STYLE[phase.status]['label']
                            );
                        },
                        width: this.secondsToMinutes(phase.duration),
                        ellipsis: '...',
                        overflow: 'truncate'
                    },
                    data: [duration],
                    tooltip: {
                        formatter: _ => {
                            return this.getTooltip(index);
                        },
                        borderWidth: 0
                    },
                    itemStyle: {
                        color: this.getPhaseColor(phase.status)
                    }
                });
            });
            return data;
        }
    },
    methods: {
        /**
         * Formats x-axis labels to show only the first and last tick labels.
         * It clamps dates to the visible interval [startDate, endDate] and
         * accounts for phases ordering coming from the API (reversed when multiple).
         *
         * @param {number} value - Tick value in minutes along the x-axis.
         * @returns {string} Formatted label in 'HH:mm' or an empty string for non-edge ticks.
         */
        formatXAxisLabel(value) {
            const isFirstTick = value === 0;
            const isLastTick = value === this.chartTotalDuration;
            if (!isFirstTick && !isLastTick) {
                return '';
            }

            const startDate = this.getStartDate;
            const endDate = this.getEndDate;

            // Defensive: no labels if there are no phases
            if (!Array.isArray(this.phases) || this.phases.length === 0) {
                return '';
            }

            const hasSinglePhase = this.phases.length === 1;
            const first = this.phases[0];
            const last = this.phases[this.phases.length - 1];

            if (hasSinglePhase) {
                if (isFirstTick) {
                    const start = new Date(first.start);
                    const labelDate = start < startDate ? startDate : start;
                    return date.formatDate(labelDate, 'HH:mm');
                }
                // last tick
                if (first.end === null) {
                    return date.formatDate(endDate, 'HH:mm');
                }
                const end = new Date(first.end);
                const labelDate = end > endDate ? endDate : end;
                return date.formatDate(labelDate, 'HH:mm');
            }

            // Multiple phases (API order reversed)
            if (isFirstTick) {
                const start = new Date(last.start);
                const labelDate = start < startDate ? startDate : start;
                return date.formatDate(labelDate, 'HH:mm');
            }
            // last tick
            if (first.end === null) {
                return date.formatDate(endDate, 'HH:mm');
            }
            const end = new Date(first.end);
            const labelDate = end > endDate ? endDate : end;
            return date.formatDate(labelDate, 'HH:mm');
        },
        /**
         * Returns the tooltip data.
         *
         * @param {number} index The index of the phase.
         * @returns {object} The tooltip data.
         */
        getTooltipData(index) {
            const phase = this.formattedPhases[index];

            const totalDuration = convertDurationToDHMS(
                phase.duration + phase.exceededDuration
            );

            const tooltipData = {
                status: this.getPhaseName(phase.status),
                product: phase.product || null,
                duration: totalDuration,
                cause: phase.cause || null,
                color: this.getPhaseColor(phase.status)
            };

            // get start date/time - show full date if not today
            const startDate = new Date(phase.start);
            const today = new Date();
            const isStartToday = date.isSameDate(startDate, today, 'day');
            tooltipData['start'] = date.formatDate(
                startDate,
                isStartToday ? 'HH:mm:ss' : 'MMM DD, HH:mm:ss'
            );

            if (phase.end) {
                // get end date/time - show full date if not today
                const endDate = new Date(phase.end);
                tooltipData['end'] = date.formatDate(
                    endDate,
                    isStartToday ? 'HH:mm:ss' : 'MMM DD, HH:mm:ss'
                );
            } else {
                tooltipData['end'] = `(${this.$t('Ongoing')})`;
            }
            return tooltipData;
        },
        /**
         * Formats the tooltip content as HTML.
         * @param {number} index The index of the phase.
         * @returns {string} The tooltip content.
         */
        getTooltip(index) {
            const tooltipData = this.getTooltipData(index);

            // Escape all dynamic string fields before interpolation to mitigate XSS
            const esc = sanitizeString;
            const markerElement = `<span style="height:10px;width:10px;background-color:${esc(tooltipData.color)};border-radius:50%;display:inline-block;"></span>`;

            let tooltipContent = `<div>${markerElement} ${esc(tooltipData.status)}</div>`;

            if (tooltipData.product) {
                tooltipContent += `<div class="text-weight-bold">${esc(tooltipData.product)}</div>`;
            }

            if (tooltipData.cause) {
                tooltipContent += `<div>${esc(tooltipData.cause)}</div>`;
            } else if (tooltipData.status === this.$t('Asset down')) {
                // status already escaped, comparison safe
                tooltipContent += `<div>${esc(this.$t('Missing cause'))}</div>`;
            }

            tooltipContent += `<div>${esc(tooltipData.start)} - ${esc(tooltipData.end)} (${esc(tooltipData.duration)})</div>`;

            return `<div class="q-gutter-sm">${tooltipContent}</div>`;
        },
        /**
         * Returns the status of the phase.
         *
         * @param {string} status The phase status.
         * @returns {string} The status of the phase.
         */
        getPhaseName(status) {
            return this.$t(ASSET_STATUS_TO_STYLE[status]['label']);
        },
        /**
         * Returns the color of the phase.
         *
         * @param {string} phase The phase.
         * @returns {string} The color of the phase.
         */
        getPhaseColor(status) {
            const statusColor = ASSET_STATUS_TO_STYLE[status]['color'];
            // get the CSS color variable from the quasar color name or use the default color
            const cssColor =
                getPaletteColor(statusColor) || getPaletteColor('secondary');
            return cssColor;
        },
        /**
         * The duration of the phase in minutes.
         * @returns {number} The duration in minutes.
         */
        secondsToMinutes(seconds) {
            return seconds / 60;
        },
        /**
         * Handle chart click events and emit the clicked phase data.
         * @param {Object} params - ECharts click event parameters
         */
        onChartClick(params) {
            if (
                params.seriesIndex !== undefined &&
                this.formattedPhases[params.seriesIndex]
            ) {
                const clickedPhase =
                    this.phases[
                        this.formattedPhases.length - 1 - params.seriesIndex
                    ];
                if (clickedPhase) {
                    this.$emit('phaseClick', clickedPhase);
                }
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.chart {
    height: 85px;
}
</style>
